import { Capacitor } from '@capacitor/core';
import { isPlatform } from '@ionic/vue';
import { useCssVar } from '@vueuse/core/index';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

import {
  FeedFilterTypeEnum,
  SendKeyEnum,
  ThemeAppEnum,
  InterfaceSizeAppEnum,
  TopicsFilterEnum,
  PagesBackgroundEnum,
  GroupsFilterEnum,
  UsersFilterEnum,
  PostsFilterEnum,
  DataViewMode,
  IdeaTypeEnum,
  PagesFilterEnum,
  UIKitFilterEnum,
} from '@/enums';
import { NotificationsFilterEnum } from '@/enums/notifications';
import { isWebMobile, isNativeMobile, useLayout } from '@/helpers';
import { DEFAULT_LOCALE, SUPPORT_LOCALES } from '@/i18n';
import { ROUTES_NAME } from '@/router';
import { $api } from '@/services';
import type { ErrorMessageModel, ResponseErrorModel, ResponseNetworkResourcesModel } from '@/types';

export type AppState = {
  appVersion: string;
  appBuild: string;

  locale: string;

  sendKey: SendKeyEnum;
  interfaceSize: InterfaceSizeAppEnum;
  theme: ThemeAppEnum;
  errors: ErrorMessageModel[];
  isLoading: boolean;
  isWaitingForCompleteLogin: boolean;
  feedType: FeedFilterTypeEnum;
  ideaType: IdeaTypeEnum;
  feedFilter: PostsFilterEnum;
  notificationsFilter: NotificationsFilterEnum;
  localNotifications: boolean;
  canPostOnBehalfMe: boolean;
  docsInNewWindow: boolean;

  appHeaderHeight: number;
  appBottomMenuHeight: number;

  companyResources: Record<string, string | null>;
  onlineUsers: number[];
  homePage: { name: string; id?: number };
  appBottomMenu: boolean;
  feedPostsCount: number;

  tagsType: TopicsFilterEnum;
  isKeyboardShown: boolean;
  pagesBackground: PagesBackgroundEnum;

  groupsFilter: GroupsFilterEnum;
  groupsViewMode: DataViewMode;
  usersFilter: { filter: UsersFilterEnum; id: number | null; subTitle: string };
  usersViewMode: DataViewMode;
  networksViewMode: DataViewMode;
  docsViewMode: DataViewMode;
  pagesViewMode: DataViewMode;
  pagesFilter: PagesFilterEnum;

  anyLongPressIsActive: boolean;
  showLoaderOnHeader: boolean;

  /** @description window.innerWidth < appSMSize
   * @note --app-sm-size: 814px
   * */
  isXSWidth: boolean;

  /** @description window.innerWidth >= app2XSSize
   * @note --app-2xs-size: 576px
   * */
  is2XSWidth: boolean;

  /** @description window.innerWidth >= appSMSize
   * @note --app-sm-size: 814px
   * */
  isSMWidth: boolean;

  /** @description window.innerWidth >= appMDSize
   * @note --app-md-size: 1004px
   * */
  isMDWidth: boolean;

  /** @description window.innerWidth >= appLGSize
   * @note --app-lg-size: 1260px
   * */
  isLGWidth: boolean;

  /** @description window.innerWidth >= appXLSize
   * @note --app-xl-size: 1420px
   * */
  isXLWidth: boolean;

  /** @description window.innerWidth >= app2XLSize
   * @note --app-2xl-size: 1660px
   * */
  is2XLWidth: boolean;

  /** @description window.innerWidth >= app3XLSize
   * @note --app-3xl-size: 1900px
   * */
  is3XLWidth: boolean;

  /** @description window.innerWidth >= app4XLSize
   * @note --app-4xl-size: 2048px
   * */
  is4XLWidth: boolean;

  appGridPadding: string;
  maxItemsInHeaderMenu: number;

  searchText: string;
  searchHistory: string[];

  appColors: string[];
  isMiddleButtonClicked: boolean;
  UIKitFilter: UIKitFilterEnum;

  fromMobilePush: boolean;

  /** @note Flag for page printing */
  isPrinting: boolean;

  /** @note Flag for changing network */
  isNetworkChanged: boolean;
};

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    appVersion: APP_VERSION,
    appBuild: APP_BUILD,
    canPostOnBehalfMe: false,
    errors: [],
    isLoading: false,
    isWaitingForCompleteLogin: false,
    feedType: FeedFilterTypeEnum.Recommended,
    ideaType: IdeaTypeEnum.New,
    feedFilter: PostsFilterEnum.All,
    notificationsFilter: NotificationsFilterEnum.All,
    localNotifications: true,
    locale: SUPPORT_LOCALES.includes(window.navigator.language.substring(0, 2).toLowerCase())
      ? window.navigator.language.substring(0, 2).toLowerCase()
      : DEFAULT_LOCALE,
    sendKey: SendKeyEnum.CtrlEnter,
    interfaceSize: InterfaceSizeAppEnum.Middle,
    appHeaderHeight: 0,
    appBottomMenuHeight: 0,
    theme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? ThemeAppEnum.Dark : ThemeAppEnum.Light,
    companyResources: {},
    homePage: { name: ROUTES_NAME.FEED },
    groupsFilter: GroupsFilterEnum.MyGroups,
    groupsViewMode: DataViewMode.Grid,
    usersFilter: { filter: UsersFilterEnum.UsersPage, id: null, subTitle: '' },
    usersViewMode: DataViewMode.Grid,
    networksViewMode: DataViewMode.List,
    docsViewMode: DataViewMode.Table,
    pagesViewMode: DataViewMode.List,
    pagesFilter: PagesFilterEnum.All,
    onlineUsers: [],
    appBottomMenu: isWebMobile || isNativeMobile,
    feedPostsCount: 20,
    tagsType: TopicsFilterEnum.Popular,
    isKeyboardShown: false,
    pagesBackground: PagesBackgroundEnum.CustomImage,
    anyLongPressIsActive: false,
    showLoaderOnHeader: false,
    docsInNewWindow: import.meta.env.VITE_ENABLE_DOCS_IN_NEW_WINDOW === 'true',

    isXSWidth: useLayout().isXSWidth(),
    is2XSWidth: useLayout().is2XSWidth(),
    isSMWidth: useLayout().isSMWidth(),
    isMDWidth: useLayout().isMDWidth(),
    isLGWidth: useLayout().isLGWidth(),
    isXLWidth: useLayout().isXLWidth(),
    is2XLWidth: useLayout().is2XLWidth(),
    is3XLWidth: useLayout().is3XLWidth(),
    is4XLWidth: useLayout().is4XLWidth(),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    appGridPadding: useCssVar('--app-grid-padding').value!,
    maxItemsInHeaderMenu: 8,

    searchText: '',
    searchHistory: [],

    appColors: [],
    isMiddleButtonClicked: false,
    UIKitFilter: UIKitFilterEnum.Intra,

    fromMobilePush: false,

    isPrinting: false,

    isNetworkChanged: false,
  }),
  getters: {
    isDarkTheme:
      () =>
      (theme: ThemeAppEnum): boolean => {
        switch (theme) {
          case ThemeAppEnum.Light:
            return false;

          case ThemeAppEnum.Dark:
            return true;

          case ThemeAppEnum.System: {
            return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? true : false;
          }
        }
      },
    isPhone(): boolean {
      const isNativePhone =
        Capacitor.isNativePlatform() && (isPlatform('iphone') || (isPlatform('android') && !isPlatform('tablet')));
      return isNativePhone || !this.isSMWidth;
    },
    getTheme: (state): ThemeAppEnum => state.theme,
    getErrors:
      (state) =>
      (type: string): string[] => {
        let _errors: string[] = [];
        state.errors
          .filter((f: ErrorMessageModel) => f.key === type)
          .forEach(function (m: ErrorMessageModel) {
            _errors = [..._errors, ...m.errors];
          });
        return _errors;
      },
    getLocalSendKey(state): SendKeyEnum {
      return state.sendKey;
    },
    getLocalInterfaceSize(state): InterfaceSizeAppEnum {
      return state.interfaceSize;
    },
    getLocalTheme(state): ThemeAppEnum {
      return state.theme;
    },
    getLocalPagesBackground(state): PagesBackgroundEnum {
      return state.pagesBackground;
    },
    getAppLogoPath(state): string {
      const logo = !this.isDarkTheme(state.theme)
        ? import.meta.env.VITE_APP_LOGO_LIGHT
        : import.meta.env.VITE_APP_LOGO_DARK;
      return `/logo/${logo}`;
    },
    getAppLogoPathWithColor:
      () =>
      (color: ThemeAppEnum): string => {
        const appStore = useAppStore();
        const logo = !appStore.isDarkTheme(color)
          ? import.meta.env.VITE_APP_LOGO_LIGHT
          : import.meta.env.VITE_APP_LOGO_DARK;
        return `/logo/${logo}`;
      },
    getAppIconPath(state): string {
      const icon = !this.isDarkTheme(state.theme)
        ? import.meta.env.VITE_APP_ICON_LIGHT
        : import.meta.env.VITE_APP_ICON_DARK;
      return `/icon/${icon}`;
    },
    getAppIconPathWithColor:
      () =>
      (color: ThemeAppEnum): string => {
        const appStore = useAppStore();
        const icon = !appStore.isDarkTheme(color)
          ? import.meta.env.VITE_APP_ICON_DARK
          : import.meta.env.VITE_APP_ICON_LIGHT;
        return `/icon/${icon}`;
      },
    getBeginChatImagePath(): string {
      const appStore = useAppStore();
      const isSMWidth = appStore.isSMWidth;
      const image = isSMWidth ? import.meta.env.VITE_CHAT_IMAGE_WEB : import.meta.env.VITE_CHAT_IMAGE_MOBILE;
      return `/images/chat/${image}`;
    },
    getBeginChatBackgroundPath(state): string {
      const image = !this.isDarkTheme(state.theme)
        ? import.meta.env.VITE_CHAT_BACKGROUND_DARK
        : import.meta.env.VITE_CHAT_BACKGROUND_LIGHT;
      return `/images/chat/${image}`;
    },
    getAppPatternPath(state): string {
      const image = !this.isDarkTheme(state.theme)
        ? import.meta.env.VITE_APP_PATTERN_DARK
        : import.meta.env.VITE_APP_PATTERN_LIGHT;
      return `/images/${image}`;
    },
    getAppNotificationSoundPath(): string {
      return `/audio/${import.meta.env.VITE_APP_NOTIFICATION_SOUND}`;
    },
    getAppCallSoundPath(): string {
      return `/audio/${import.meta.env.VITE_APP_CALL_SOUND}`;
    },
    getLocalFeedType(state): FeedFilterTypeEnum {
      return state.feedType;
    },
    getLocalIdeaType(state): IdeaTypeEnum {
      return state.ideaType;
    },
  },
  actions: {
    //#region Setters
    setIsNetworkChanged(state: boolean) {
      this.isNetworkChanged = state;
    },
    setIsWaitingForCompleteLogin(state: boolean) {
      this.isWaitingForCompleteLogin = state;
    },
    setIsLoading(state: boolean) {
      this.isLoading = state;
    },
    setAppBottomMenu(state: boolean) {
      this.appBottomMenu = state;
    },
    setAppInfo(version: string, build: string) {
      this.appVersion = version;
      this.appBuild = build;
    },
    setMiddleButtonClicked(state: boolean) {
      this.isMiddleButtonClicked = state;
    },
    setFeedType(feedType: FeedFilterTypeEnum) {
      this.feedType = feedType;
    },
    setKeyboardShown(state: boolean) {
      this.isKeyboardShown = state;
    },
    setShowLoaderOnHeader(state: boolean) {
      this.showLoaderOnHeader = state;
    },
    setTheme(theme: ThemeAppEnum) {
      switch (theme) {
        case ThemeAppEnum.Light:
          {
            this.$patch({
              theme: ThemeAppEnum.Light,
            });
            document.body.classList.remove('dark');
          }
          break;

        case ThemeAppEnum.Dark:
          {
            this.$patch({
              theme: ThemeAppEnum.Dark,
            });
            document.body.classList.add('dark');
          }
          break;

        case ThemeAppEnum.System:
          {
            const preparedTheme = window.matchMedia?.('(prefers-color-scheme: dark)').matches
              ? ThemeAppEnum.Dark
              : ThemeAppEnum.Light;
            this.$patch({
              theme: ThemeAppEnum.System,
            });
            preparedTheme === ThemeAppEnum.Dark
              ? document.body.classList.add('dark')
              : document.body.classList.remove('dark');
          }
          break;

        default:
          break;
      }
    },
    setBackground(background: PagesBackgroundEnum) {
      switch (background) {
        case PagesBackgroundEnum.CustomImage:
          this.$patch({
            pagesBackground: PagesBackgroundEnum.CustomImage,
          });
          break;

        case PagesBackgroundEnum.Default:
          this.$patch({
            pagesBackground: PagesBackgroundEnum.Default,
          });
          break;

        default:
          break;
      }
    },
    setOnlineUsers(data: number[]) {
      this.onlineUsers = data;
    },
    setUserConnected(userId: number) {
      const index = this.onlineUsers.findIndex((u: number) => u === userId);
      if (index < 0) {
        this.onlineUsers.push(userId);
      }
    },
    setUserDisconnected(userId: number) {
      this.onlineUsers = this.onlineUsers.filter((u: number) => u !== userId);
    },
    setIsPrinting(value: boolean) {
      this.isPrinting = value;
    },
    setUsersFilter(filter: UsersFilterEnum, id: number | null, subTitle: string) {
      this.usersFilter = { filter, id, subTitle };
    },
    setDocsInNewWindow(state: boolean) {
      this.docsInNewWindow = state;
    },
    //#endregion

    //#region API
    /**
     * @todo Should be moved to `src/store/network.pinia.ts`
     */
    async getCompanyResources(): Promise<Record<string, string | null>> {
      const response = await $api.network.getResources();

      if (response.statusCode === 200) {
        const model = response as ResponseNetworkResourcesModel;
        this.companyResources = cloneDeep(model.resources);
        return model.resources;
      }

      if (response.statusCode !== 200) {
        const error = response as ResponseErrorModel;
        this.errors = cloneDeep(error.errorMessages);
      }

      return {};
    },
    //#endregion
  },
  persist: true,
});
